import { ConfirmationResult, RecaptchaVerifier, UserCredential } from "firebase/auth";
import { Observable, WritableObservable } from "micro-observables";
import { AuthProvider, SocialAuthProvider } from "@domain/auth/authProvider";
import { AuthService } from "@domain/auth/authService";
import { ProfileData, ProfileRole } from "@domain/user/profile";
import { Person } from "@domain/user/user";
import { clearAskedToCompleteProfile } from "./completeProfilePrompt";
import { CountryIndicator } from "./countriesIndicators";
import { CurrentProfile, UserGender } from "./currentProfile";
import { CurrentProfileApi } from "./currentProfileApi";
import { InitializationState } from "./initializationState";
import { EmailProfileDraft, PhoneNumberProfileDraft } from "./profileDraft";
import { RegisterState } from "./registerState";

export class CurrentProfileService {
	private _currentProfile = new WritableObservable<CurrentProfile | null>(null);

	initialization = new WritableObservable(InitializationState.IDLE);
	organizationAlias = new WritableObservable<Person | null>(null);

	private _registerStateUnsubscribe?: () => void;

	get currentProfile(): Observable<CurrentProfile | null> {
		return this._currentProfile.readOnly();
	}
	get registerState(): Observable<RegisterState> {
		return Observable.select(
			[this.authService.firebaseUser, this.currentProfile, this.initialization],
			(firebaseUser, profile, initialization) => {
				if (initialization !== InitializationState.FINISHED) {
					return firebaseUser ? RegisterState.LOGGED : RegisterState.LOGOUT;
				}
				if (!firebaseUser) {
					return RegisterState.LOGOUT;
				}
				if (!firebaseUser.emailVerified && !firebaseUser.phoneNumber) {
					return RegisterState.VERIFY;
				}
				if (!profile) {
					return RegisterState.INFOS;
				}
				if (!profile.customData?.onboardingFinished) {
					return RegisterState.ROLE;
				}
				return RegisterState.FINISHED;
			}
		);
	}

	constructor(
		private currentProfileApi: CurrentProfileApi,
		private authService: AuthService,
		initializationState?: InitializationState
	) {
		this.authService.onFirebaseUserChange.add((user) => this.initCurrentProfile(user));
		if (initializationState) {
			this.initialization.set(initializationState);
		}
	}

	async register(email: string, password: string, firstName: string, lastName: string): Promise<void> {
		const user = await this.authService.register(email, password);
		if (user) {
			await this.createProfile(email, firstName, lastName);
		}
	}

	async startEmailSignup(email: string, password: string): Promise<void> {
		const user = await this.authService.register(email, password);
		if (user) {
			await this.authService.sendVerifyEmail();
		}
	}

	async signIn(authProvider: SocialAuthProvider): Promise<void> {
		await this.authService.auth(authProvider);
	}

	async createProfile(email: string, firstName?: string | null, lastName?: string, verified?: boolean): Promise<void> {
		const currentProfile = await this.currentProfileApi.register({
			email,
			primaryName: firstName ?? email.split("@")[0],
			secondaryName: lastName,
			description: "",
		} as EmailProfileDraft);
		this._currentProfile.set(currentProfile);
		if (!verified) {
			await this.authService.sendVerifyEmail();
		}
	}
	async createProfileWithPhoneNumber(phoneNumber: string, primaryName: string, secondaryName: string): Promise<void> {
		const currentProfile = await this.currentProfileApi.register({
			phoneNumberAuth: phoneNumber,
			primaryName,
			secondaryName,
			description: "",
		} as PhoneNumberProfileDraft);
		this._currentProfile.set(currentProfile);
	}

	login(authProvider: SocialAuthProvider): Promise<void>;
	login(authProvider: "Tooshare", email: string, password: string): Promise<void>;
	async login(authProvider: AuthProvider, email?: string, password?: string): Promise<void> {
		if (authProvider === "Tooshare") {
			if (!email || !password) {
				return;
			}
			return this.authService.login(email, password);
		}
		const newUser = await this.authService.auth(authProvider);
		if (newUser?.email && newUser?.isNewUser) {
			const { firstName, lastName } = splitDisplayName(newUser.displayName);
			this.createProfile(newUser.email, firstName, lastName, !!newUser?.emailVerified);
		}
	}

	async setupRecaptcha(
		number: string,
		isRecaptchaDisplay?: boolean
	): Promise<{ confirm: ConfirmationResult; recaptcha: RecaptchaVerifier } | undefined> {
		return await this.authService.setupRecaptcha(number, isRecaptchaDisplay);
	}

	async checkUserWithPhoneNumber(confirm: ConfirmationResult, code: string): Promise<UserCredential> {
		const result = await confirm.confirm(code);
		return result;
	}
	async loginWithPhoneNumber(phoneNumber: string, primaryName: string, secondaryName: string): Promise<void> {
		await this.createProfileWithPhoneNumber(phoneNumber, primaryName, secondaryName);
	}

	async linkPhoneNumber(confirm: ConfirmationResult, code: string): Promise<void> {
		const result = await this.authService.linkPhoneNumber(confirm, code);

		if (result) {
			await this.saveProfileInfos({ phoneNumberAuth: result.user?.phoneNumber as string });
		}
	}

	async updatePhoneNumber(
		confirm: ConfirmationResult,
		code: string,
		indicator: CountryIndicator,
		phoneNumber: string
	): Promise<boolean> {
		try {
			const result = await this.authService.updatePhoneNumber(confirm, code);
			if (result) {
				await this.saveProfileInfos({ phoneNumberAuth: `+` + indicator + phoneNumber });
				return true;
			}
			return false;
		} catch {
			return false;
		}
	}

	async updateEmail(email: string): Promise<void> {
		return this.currentProfileApi.updateEmail(email);
	}

	async saveGenderChoice(choice: UserGender | "OTHER"): Promise<void> {
		const currentProfile = this._currentProfile.get();
		if (!currentProfile) {
			throw Error("Current profile not found");
		}
		if (choice === "OTHER") {
			if (currentProfile.gender) {
				await this.saveProfileInfos({ gender: undefined });
			}
			const latest = this._currentProfile.get() ?? currentProfile;
			const customData: ProfileData = {
				...latest.customData,
				askedGender: true,
			};
			await this.currentProfileApi.store(customData);
			this.update((profile) => ({ ...profile, gender: undefined, customData }));
			return;
		}
		await this.saveProfileInfos({ gender: choice });
		const latest = this._currentProfile.get();
		if (!latest?.customData?.askedGender) {
			return;
		}
		const { askedGender: _askedGender, ...customData } = latest.customData;
		await this.currentProfileApi.store(customData);
		this.update((profile) => ({ ...profile, customData }));
	}

	async saveProfileInfos(infos: Partial<CurrentProfile>): Promise<CurrentProfile> {
		const currentProfile = this._currentProfile.get();

		if (!currentProfile) {
			throw Error("Current profile not found");
		}

		const newProfile = await this.currentProfileApi.update({ ...currentProfile, ...infos });
		this._currentProfile.set(newProfile);
		return newProfile;
	}

	async finishOnboarding(profileRole?: ProfileRole): Promise<void> {
		const currentProfile = this._currentProfile.get();
		if (!currentProfile) {
			return;
		}
		if (profileRole) {
			await this.saveProfileInfos({ profileRole });
		}
		const latestProfile = this._currentProfile.get();
		if (!latestProfile) {
			return;
		}
		const customData = {
			...latestProfile.customData,
			onboardingFinished: true,
			...(profileRole ? { profileRole } : {}),
		};
		await this.currentProfileApi.store(customData);
		this.update((c) => ({ ...c, customData }));
	}

	logout(): Promise<void> {
		return this.authService.signOut().finally(() => {
			clearAskedToCompleteProfile();
		});
	}

	update(updater: (current: CurrentProfile) => CurrentProfile): void {
		this._currentProfile.update((current) => (current ? updater(current) : null));
	}

	sendRecoveryEmail(email: string): Promise<void> {
		return this.authService.sendRecoveryEmail(email);
	}

	changeEmail(email: string): Promise<void> {
		return this.currentProfileApi.changeEmail(email);
	}

	async delete(): Promise<void> {
		await this.currentProfileApi.delete();
	}

	setAlias(alias: Person | null): void {
		this.organizationAlias.set(alias);
	}

	getAlias(): WritableObservable<Person | null> {
		return this.organizationAlias;
	}

	setPostLoginLocation(path: string): void {
		this._registerStateUnsubscribe = this.registerState.onChange((state) => {
			if (state === RegisterState.FINISHED) {
				window.location.replace(path);
				this._registerStateUnsubscribe?.();
			}
		});
	}

	private async initCurrentProfile(
		user: {
			email: string | null;
			displayName: string | null;
			phoneNumber: string | null;
		} | null
	): Promise<void> {
		if (user) {
			this.initialization.set(InitializationState.PENDING);
			try {
				const currentProfile = await this.currentProfileApi.getCurrentProfile();
				this._currentProfile.set(currentProfile);
			} catch {
				if (!user.email && !user.phoneNumber) {
					this.logout();
					return;
				}
			}
		} else {
			this._currentProfile.set(null);
		}

		this.initialization.set(InitializationState.FINISHED);
	}
}

function splitDisplayName(displayName?: string | null): { firstName?: string; lastName?: string } {
	const name = displayName?.trim();
	if (!name) {
		return {};
	}
	const space = name.indexOf(" ");
	if (space === -1) {
		return { firstName: name };
	}
	const lastName = name.slice(space + 1).trim();
	return { firstName: name.slice(0, space), lastName: lastName || undefined };
}
